import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import UserMenu from './UserMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PendingUsersPage = () => {
  const { user } = useAuth();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingUser, setProcessingUser] = useState(null);

  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'lead')) {
      loadPendingUsers();
    }
  }, [user]);

  const loadPendingUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPendingUsers(response.data.pending_users || []);
    } catch (error) {
      console.error('Failed to load pending users:', error);
    } finally {
      setLoading(false);
    }
  };

  const approveUser = async (userId, userName, userEmail) => {
    if (!window.confirm(`Одобрить доступ для ${userName} (${userEmail})?`)) {
      return;
    }
    
    try {
      setProcessingUser(userId);
      
      const response = await axios.put(`${API}/admin/users/${userId}/approve`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      alert(`Пользователь ${userName} одобрен! Email отправлен.`);
      
      // Remove from pending list
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
      
    } catch (error) {
      console.error('Failed to approve user:', error);
      alert('Ошибка при одобрении пользователя: ' + (error.response?.data?.detail || 'Неизвестная ошибка'));
    } finally {
      setProcessingUser(null);
    }
  };

  const rejectUser = async (userId, userName, userEmail) => {
    const reason = prompt(`Причина отклонения заявки ${userName} (${userEmail}):`);
    
    if (reason === null) {
      return; // User canceled
    }
    
    try {
      setProcessingUser(userId);
      
      const response = await axios.put(`${API}/admin/users/${userId}/reject`, {
        reason: reason
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      alert(`Пользователь ${userName} отклонен. Email отправлен.`);
      
      // Remove from pending list
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
      
    } catch (error) {
      console.error('Failed to reject user:', error);
      alert('Ошибка при отклонении пользователя: ' + (error.response?.data?.detail || 'Неизвестная ошибка'));
    } finally {
      setProcessingUser(null);
    }
  };

  if (!user || (user.role !== 'admin' && user.role !== 'lead')) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <UserMenu />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Доступ запрещён</h1>
            <p className="text-gray-600">Только администраторы и руководители могут управлять заявками пользователей.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <UserMenu />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-6">
            <div className="flex items-center mb-6">
              <svg className="w-8 h-8 mr-3 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Заявки на доступ</h1>
                <p className="text-gray-600">Управление доступом пользователей к системе</p>
              </div>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-orange-50 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="ml-4">
                    <div className="text-2xl font-bold text-orange-600">{pendingUsers.length}</div>
                    <div className="text-sm text-orange-700">Ожидают одобрения</div>
                  </div>
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="ml-4">
                    <div className="text-2xl font-bold text-green-600">✓</div>
                    <div className="text-sm text-green-700">Быстрое одобрение</div>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <div className="ml-4">
                    <div className="text-2xl font-bold text-blue-600">📧</div>
                    <div className="text-sm text-blue-700">Email уведомления</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pending Users List */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Пользователи, ожидающие одобрения ({pendingUsers.length})
              </h2>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Загрузка заявок...</p>
              </div>
            ) : pendingUsers.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Нет заявок на рассмотрение</h3>
                <p className="text-gray-600">Все пользователи одобрены или нет новых заявок</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {pendingUsers.map((pendingUser) => (
                  <div key={pendingUser.id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {/* Avatar */}
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-lg">
                            {pendingUser.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        
                        {/* User Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-medium text-gray-900 truncate">
                            {pendingUser.name}
                          </h3>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span className="flex items-center">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              {pendingUser.email}
                            </span>
                            <span className="flex items-center">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {new Date(pendingUser.created_at).toLocaleDateString('ru-RU', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <div className="mt-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              Запрошенная роль: {
                                pendingUser.role === 'manager' ? 'Менеджер' :
                                pendingUser.role === 'lead' ? 'Руководитель' :
                                pendingUser.role === 'admin' ? 'Администратор' :
                                pendingUser.role
                              }
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => approveUser(pendingUser.id, pendingUser.name, pendingUser.email)}
                          disabled={processingUser === pendingUser.id}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                          {processingUser === pendingUser.id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Обработка...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Одобрить
                            </>
                          )}
                        </button>
                        
                        <button
                          onClick={() => rejectUser(pendingUser.id, pendingUser.name, pendingUser.email)}
                          disabled={processingUser === pendingUser.id}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          {processingUser === pendingUser.id ? (
                            'Обработка...'
                          ) : (
                            <>
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              Отклонить
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="mt-6 bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">ℹ️ Инструкции по управлению доступом</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-800 mb-2">✅ Одобрение пользователей</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Новые пользователи автоматически получают роль "Менеджер"</li>
                  <li>• После одобрения пользователь получает email уведомление</li>
                  <li>• Роль можно изменить позже через "Управление пользователями"</li>
                  <li>• Одобренные пользователи сразу могут войти в систему</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-gray-800 mb-2">❌ Отклонение заявок</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Укажите причину отклонения для пользователя</li>
                  <li>• Пользователь получит email с объяснением</li>
                  <li>• Отклоненные пользователи не смогут войти в систему</li>
                  <li>• Статус можно изменить через "Управление пользователями"</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Важно:</strong> Новые регистрации требуют обязательного одобрения администратором или руководителем. 
                Пользователи не могут войти в систему до получения одобрения.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingUsersPage;